<div class="create-plan-page">
  <!-- Header -->
  <div class="page-header">
    <div class="header-left">
      <button type="button" class="back-button" (click)="goBack()" title="Back">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        <span>{{ isEditMode ? "Back to Plan Details" : "Back to Plans" }}</span>
      </button>

      <div class="title-section">
        <h1 class="page-title">
          {{
            isEditMode ? "Edit Subscription Plan" : "Create Subscription Plan"
          }}
        </h1>
        <p class="page-subtitle">
          Configure subscription tier details, pricing schedules, eligible
          organizations, and access permissions.
        </p>
      </div>
    </div>
  </div>

  @if (isLoading) {
    <div class="skeleton-form-grid">
      <div class="skeleton-card large"></div>
      <div class="skeleton-card medium"></div>
      <div class="skeleton-card medium"></div>
    </div>
  } @else {
    <div class="form-container">
      <!-- Section 1: Plan Details -->
      <section class="form-card">
        <div class="card-header">
          <h2 class="card-title">Plan Identification</h2>
        </div>

        <div class="form-grid">
          <!-- Plan Slug -->
          <div class="form-field">
            <label class="form-label" for="planName">
              Plan Slug (System Name) <span class="required">*</span>
            </label>
            <input
              id="planName"
              type="text"
              class="form-input"
              [class.input-error]="createForm.plan_name.error"
              placeholder="e.g. premium_tier"
              [(ngModel)]="createForm.plan_name.value"
              (ngModelChange)="validatePlanName($event)"
              (blur)="validatePlanName()"
              [disabled]="isEditMode"
            />
            <div class="field-hint">
              Unique lowercase identifier. Letters, numbers, and underscores
              only.
            </div>
            @if (createForm.plan_name.error === "required") {
              <div class="field-error">Plan slug is required.</div>
            }
            @if (createForm.plan_name.error === "textValidation") {
              <div class="field-error">
                {{ createForm.plan_name.validators["textValidation"] }}
              </div>
            }
          </div>

          <!-- Display Name -->
          <div class="form-field">
            <label class="form-label" for="displayName">
              Display Name <span class="required">*</span>
            </label>
            <input
              id="displayName"
              type="text"
              class="form-input"
              [class.input-error]="createForm.display_name.error"
              placeholder="e.g. Premium Tier"
              [(ngModel)]="createForm.display_name.value"
              (ngModelChange)="validateDisplayName($event)"
              (blur)="validateDisplayName()"
            />
            <div class="field-hint">
              User-facing title shown across the portal.
            </div>
            @if (createForm.display_name.error === "required") {
              <div class="field-error">Display name is required.</div>
            }
          </div>

          <!-- Trial Period -->
          <div class="form-field full-width">
            <label class="form-label" for="trialPeriodDays">
              Trial Period (Days)
            </label>
            <div class="trial-input-wrap">
              <input
                id="trialPeriodDays"
                type="number"
                class="form-input trial-input"
                [class.input-error]="createForm.trial_period_days.error"
                placeholder="0"
                min="0"
                max="365"
                step="1"
                [(ngModel)]="createForm.trial_period_days.value"
                (ngModelChange)="validateTrialPeriodDays($event)"
                (blur)="validateTrialPeriodDays()"
              />
              <span class="input-suffix">Days</span>
            </div>
            <div class="field-hint">
              Enter 0 for no trial. Admins can initiate trial periods for
              eligible connections from the plan details page.
            </div>
            @if (createForm.trial_period_days.error === "range") {
              <div class="field-error">
                {{ createForm.trial_period_days.validators["range"] }}
              </div>
            }
          </div>
        </div>
      </section>

      <!-- Section 2: Pricing -->
      <section class="form-card">
        <div class="card-header">
          <h2 class="card-title">Pricing Schedule</h2>
          @if (isEditMode) {
            <p class="section-note">
              Pricing cannot be modified on existing plans.
            </p>
          }
        </div>

        <div class="form-grid">
          <!-- Monthly Price -->
          <div class="form-field">
            <label class="form-label" for="monthlyPrice">
              Monthly Price ($) <span class="required">*</span>
            </label>
            <div class="currency-input-wrap">
              <span class="currency-prefix">$</span>
              <input
                id="monthlyPrice"
                type="number"
                class="form-input currency-input"
                [class.input-error]="createForm.month_price.error"
                placeholder="0.00"
                min="0"
                step="1"
                [(ngModel)]="createForm.month_price.value"
                (ngModelChange)="onMonthlyPriceChange($event)"
                (blur)="onMonthlyPriceBlur()"
                [disabled]="isEditMode"
              />
              <span class="cycle-suffix">/ month</span>
            </div>
            @if (createForm.month_price.error === "required") {
              <div class="field-error">Monthly price is required.</div>
            }
            @if (createForm.month_price.error === "greaterThanPrice") {
              <div class="field-error">
                {{ createForm.month_price.validators["greaterThanPrice"] }}
              </div>
            }
            @if (createForm.month_price.error === "lessThanPrice") {
              <div class="field-error">
                {{ createForm.month_price.validators["lessThanPrice"] }}
              </div>
            }
          </div>

          <!-- Yearly Price -->
          <div class="form-field">
            <label class="form-label" for="yearlyPrice">
              Yearly Price ($)
            </label>
            <div class="currency-input-wrap">
              <span class="currency-prefix">$</span>
              <input
                id="yearlyPrice"
                type="number"
                class="form-input currency-input"
                [class.input-error]="createForm.year_price.error"
                placeholder="Auto-calculated (Monthly × 12)"
                min="0"
                step="1"
                [(ngModel)]="createForm.year_price.value"
                (ngModelChange)="validateYearlyPrice($event)"
                (blur)="validateYearlyPrice()"
                [disabled]="isEditMode"
              />
              <span class="cycle-suffix">/ year</span>
            </div>
            <div class="field-hint">
              Auto-calculated as Monthly Price × 12. You can customize this
              amount.
            </div>
            @if (createForm.year_price.error === "greaterThanPrice") {
              <div class="field-error">
                {{ createForm.year_price.validators["greaterThanPrice"] }}
              </div>
            }
            @if (createForm.year_price.error === "lessThanPrice") {
              <div class="field-error">
                {{ createForm.year_price.validators["lessThanPrice"] }}
              </div>
            }
          </div>
        </div>
      </section>

      <!-- Section 3: Eligible Organizations & Admin Assignment -->
      <section class="form-card">
        <div class="card-header">
          <h2 class="card-title">
            Eligible Organizations & Admins ({{
              createForm.connections.value.length
            }})
          </h2>
          <p class="section-note">
            Assign organizations eligible for this plan and designate an admin
            to manage billing.
          </p>
        </div>

        <div class="connection-section-body">
          <div class="form-field full-width">
            <label class="form-label" for="connectionsList">
              Assign Organizations
            </label>
            <app-multiselect-filter
              id="connectionsList"
              class="full-width-filter"
              [options]="connectionsList"
              [selectedValues]="createForm.connections.value"
              [placeholder]="'Select organizations to assign...'"
              [optionKey]="'connection_display_name'"
              [valueKey]="'connection_name'"
              [styleClass]="'cdfi-multiselect'"
              [fallBackLabel]="true"
              (apply)="onApplyConnections($event)"
            >
            </app-multiselect-filter>
          </div>

          @if (createForm.connections.value.length > 0) {
            <div class="assigned-chips-section">
              <div class="chips-header">
                <span class="chips-count">
                  {{ createForm.connections.value.length }} assigned
                  organization{{
                    createForm.connections.value.length === 1 ? "" : "s"
                  }}
                </span>
              </div>

              <div class="chips-wrapper">
                @for (
                  connectionName of createForm.connections.value;
                  track connectionName
                ) {
                  @let adminUsers = getAdminUsers(connectionName);
                  @let hasAdmins = adminUsers.length > 0;
                  <div
                    class="cdfi-chip"
                    [class.has-admin]="hasAdmins"
                    [class.needs-admin]="!hasAdmins"
                  >
                    <!-- Status Icon -->
                    @if (hasAdmins) {
                      <span
                        class="chip-status-icon success"
                        [title]="
                          'Admins assigned: ' + getAdminTooltip(connectionName)
                        "
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                    } @else {
                      <span
                        class="chip-status-icon neutral"
                        title="No admin assigned"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path
                            d="M3 21h18M3 7v14M21 7v14M6 11h.01M6 15h.01M10 11h.01M10 15h.01M14 11h.01M14 15h.01M18 11h.01M18 15h.01M8 3h8l3 4H5z"
                          />
                        </svg>
                      </span>
                    }

                    <span
                      class="chip-name"
                      [title]="getConnectionDisplayName(connectionName)"
                    >
                      {{ getConnectionDisplayName(connectionName) }}
                    </span>

                    @if (hasAdmins) {
                      @if (adminUsers.length === 1) {
                        <span
                          class="chip-admin-tag"
                          [title]="'Admin: ' + getAdminUserLabel(adminUsers[0])"
                        >
                          <svg
                            class="admin-tag-icon"
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path
                              d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                            />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          <span class="admin-text">{{
                            getAdminUserLabel(adminUsers[0])
                          }}</span>
                        </span>
                      } @else if (adminUsers.length === 2) {
                        @for (admin of adminUsers; track admin.id) {
                          <span
                            class="chip-admin-tag"
                            [title]="'Admin: ' + getAdminUserLabel(admin)"
                          >
                            <svg
                              class="admin-tag-icon"
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path
                                d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                              />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                            <span class="admin-text">{{
                              getAdminUserLabel(admin)
                            }}</span>
                          </span>
                        }
                      } @else {
                        <span
                          class="chip-admin-tag"
                          [title]="'Admin: ' + getAdminUserLabel(adminUsers[0])"
                        >
                          <svg
                            class="admin-tag-icon"
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path
                              d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                            />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          <span class="admin-text">{{
                            getAdminUserLabel(adminUsers[0])
                          }}</span>
                        </span>
                        <span
                          class="chip-admin-tag more-tag"
                          [title]="getAdminTooltip(connectionName)"
                        >
                          +{{ adminUsers.length - 1 }} more
                        </span>
                      }
                    } @else {
                      <span class="chip-unassigned-tag">
                        <span>No Admin</span>
                      </span>
                    }

                    <button
                      type="button"
                      class="chip-admin-btn"
                      [class.has-admin]="hasAdmins"
                      [class.needs-admin]="!hasAdmins"
                      [title]="
                        (hasAdmins ? 'Manage' : 'Assign') +
                        ' subscription admins for ' +
                        getConnectionDisplayName(connectionName)
                      "
                      (click)="openAdminPicker(connectionName)"
                    >
                      @if (hasAdmins) {
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path
                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                          />
                          <path
                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                          />
                        </svg>
                        <span>Manage</span>
                      } @else {
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        <span>Assign Admin</span>
                      }
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </section>

      <!-- Section 4: Permissions -->
      <section class="form-card">
        <div class="card-header">
          <h2 class="card-title">
            Feature Permissions <span class="required">*</span>
          </h2>
          <p class="section-note">
            Enable the features and capabilities authorized for subscribers of
            this plan.
          </p>
        </div>

        <div class="permissions-container">
          <div class="permissions-list">
            @for (permission of permissionList; track permission.id) {
              <div class="permission-row">
                <div class="permission-meta">
                  <span class="permission-name">{{ permission.name }}</span>
                </div>

                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    [checked]="isPermissionChecked(permission)"
                    (change)="
                      togglePermission(permission, $any($event.target).checked)
                    "
                  />
                  <span class="toggle-slider"></span>
                </label>
              </div>
            }
          </div>

          @if (createForm.permissions.error === "atleatOne") {
            <div class="field-error permissions-error">
              At least one feature permission must be enabled for this plan.
            </div>
          }
        </div>
      </section>

      <!-- Form Actions Footer -->
      <div class="form-actions-bar">
        <button
          type="button"
          class="btn-cancel"
          (click)="goBack()"
          [disabled]="isSaving"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn-submit"
          (click)="onSavePlan()"
          [disabled]="isSaving"
        >
          @if (isSaving) {
            <span>Saving...</span>
          } @else {
            <span>{{ isEditMode ? "Save Changes" : "Create Plan" }}</span>
          }
        </button>
      </div>
    </div>
  }
</div>

<!-- Admin Picker Modal -->
@if (adminPickerConnection) {
  <app-popup
    class="admin-picker-popup"
    [heading]="
      'Manage Admins: ' + getConnectionDisplayName(adminPickerConnection)
    "
    [confirmText]="'Save Admins'"
    [cancelText]="'Cancel'"
    [isConfirmDisabled]="isLoadingPickerUsers"
    [showCloseIcon]="true"
    (confirmClicked)="confirmAdminSelection()"
    (cancelClicked)="cancelAdminSelection()"
  >
    <div class="admin-picker-content">
      <div class="admin-picker-header-meta">
        <p class="admin-picker-subtitle">
          Select users from this organization who can manage plans, billing, and
          transactions. Multiple admins can be designated.
        </p>
        @if ((connectionUsersByName[adminPickerConnection] || []).length > 0) {
          <div class="admin-picker-toolbar">
            <span class="selection-count">
              {{ tempAdminSelections.length }} of
              {{ (connectionUsersByName[adminPickerConnection] || []).length }}
              selected
            </span>
            <div class="toolbar-actions">
              <button
                type="button"
                class="btn-text-action"
                (click)="selectAllPickerUsers()"
              >
                Select All
              </button>
              <span class="divider">·</span>
              <button
                type="button"
                class="btn-text-action"
                (click)="clearPickerSelections()"
              >
                Clear
              </button>
            </div>
          </div>
        }
      </div>

      <div class="admin-picker-body">
        @if ((connectionUsersByName[adminPickerConnection] || []).length > 0) {
          <ul
            class="admin-user-list"
            role="listbox"
            aria-multiselectable="true"
          >
            @for (
              user of connectionUsersByName[adminPickerConnection] || [];
              track user.id
            ) {
              @let isSelected = isPickerUserSelected(user.id);
              <li>
                <button
                  type="button"
                  class="admin-user-option"
                  role="option"
                  [class.selected]="isSelected"
                  [attr.aria-selected]="isSelected"
                  (click)="togglePickerUser(user.id)"
                >
                  <span
                    class="admin-user-checkbox"
                    [class.checked]="isSelected"
                    aria-hidden="true"
                  >
                    @if (isSelected) {
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    }
                  </span>
                  <span class="admin-user-meta">
                    <span class="admin-user-name">
                      {{ user.first_name }} {{ user.last_name }}
                    </span>
                    <span class="admin-user-email">{{ user.email }}</span>
                  </span>
                </button>
              </li>
            }
          </ul>
        } @else if (isLoadingPickerUsers) {
          <div class="admin-picker-empty">
            <div class="spinner-small"></div>
            <span>Loading organization users...</span>
          </div>
        } @else {
          <div class="admin-picker-empty">
            No users found registered under this organization.
          </div>
        }
      </div>
    </div>
  </app-popup>
}

<!-- Permissions Change Confirm Popup (Edit Mode) -->
@if (showPermissionsChangeConfirm) {
  <app-popup
    [heading]="'Permissions Change Warning'"
    [confirmText]="'Proceed & Save'"
    [cancelText]="'Review Changes'"
    (confirmClicked)="onConfirmPermissionsChange()"
    (cancelClicked)="showPermissionsChangeConfirm = false"
  >
    <div class="permissions-confirm-content">
      <p>
        Modifying feature permissions will immediately affect all existing
        Organization accounts currently subscribed to this plan.
      </p>
      <p class="warning-subtext">
        Active users may need to log out and log back in to reflect newly added
        or removed permissions.
      </p>
    </div>
  </app-popup>
}
